import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthLoginResponse, AuthResponse, AuthService } from './auth.service';

@Component({
  selector: 'app-irrigator-auth',
  imports: [FormsModule],
  templateUrl: './irrigator-auth.component.html'
})
export class IrrigatorAuthComponent {
  @Output() authenticated = new EventEmitter<AuthResponse>();
  @Output() backRequested = new EventEmitter<void>();

  protected irrigatorAuthMethod: 'receipt' | 'certificate' = 'receipt';
  protected ticketCode = '';
  protected receiptAmount = '';
  protected irrigatorAuthError = '';
  protected irrigatorAuthLoading = false;

  private readonly authService = inject(AuthService);

  protected get digitalCertificateAuthUrl(): string {
    return this.authService.getCertificateLoginUrl();
  }

  protected backToProfileSelection(): void {
    this.irrigatorAuthMethod = 'receipt';
    this.irrigatorAuthError = '';
    this.backRequested.emit();
  }

  protected async authenticateIrrigatorByReceipt(event: Event): Promise<void> {
    event.preventDefault();
    this.irrigatorAuthError = '';

    const ticket = this.ticketCode.trim();
    const receiptAmount = this.parseReceiptAmount(this.receiptAmount);

    if (!ticket || ticket.length > 8) {
      this.irrigatorAuthError = 'El codigo indicado en el documento de pago es obligatorio y debe tener como maximo 8 caracteres.';
      return;
    }

    if (receiptAmount === undefined) {
      this.irrigatorAuthError = 'El importe del recibo debe ser un numero entre 0,01 y 999999999.';
      return;
    }

    this.irrigatorAuthLoading = true;

    try {
      const response = await this.authService.loginIrrigatorByReceipt(ticket, receiptAmount);

      if (response.ok) {
        const loginResponse = await response.json() as AuthLoginResponse;

        if (this.authService.isAuthResponse(loginResponse)) {
          this.authenticated.emit(loginResponse);
          return;
        }

        if (this.authService.isSecondFactorRequiredResponse(loginResponse)) {
          this.irrigatorAuthError = 'Este acceso requiere doble factor. Actualiza el portal para completar la autenticacion.';
          return;
        }

        if (this.authService.isSecondFactorEnrollmentRequiredResponse(loginResponse)) {
          this.irrigatorAuthError = 'Este acceso requiere configurar doble factor. Actualiza el portal para completar la autenticacion.';
          return;
        }

        this.irrigatorAuthError = 'No se ha podido completar la autenticacion. Respuesta no reconocida.';
        return;
      }

      if (response.status === 401) {
        this.irrigatorAuthError = 'El codigo indicado o el importe del recibo no son correctos.';
        return;
      }

      if (response.status === 403) {
        const errorCode = await this.readErrorCode(response);
        this.irrigatorAuthError = this.getForbiddenIrrigatorLoginMessage(errorCode);
        return;
      }

      this.irrigatorAuthError = 'No se ha podido completar la autenticacion. Intentalo de nuevo mas tarde.';
    } catch {
      this.irrigatorAuthError = 'No se ha podido contactar con el servicio de autenticacion.';
    } finally {
      this.irrigatorAuthLoading = false;
    }
  }

  private parseReceiptAmount(value: string): number | undefined {
    const normalizedValue = this.normalizeReceiptAmount(value);

    if (!normalizedValue) {
      return undefined;
    }

    const parsedValue = Number(normalizedValue);

    if (!Number.isFinite(parsedValue) || parsedValue < 0.01 || parsedValue > 999999999) {
      return undefined;
    }

    return Math.round(parsedValue * 100) / 100;
  }

  private normalizeReceiptAmount(value: string): string | undefined {
    const compactValue = value.trim().replace(/[\s\u00a0']/g, '');

    if (!compactValue || !/^\d+(?:[.,]\d+)*$/.test(compactValue)) {
      return undefined;
    }

    const lastCommaIndex = compactValue.lastIndexOf(',');
    const lastDotIndex = compactValue.lastIndexOf('.');

    if (lastCommaIndex >= 0 && lastDotIndex >= 0) {
      const decimalSeparator = lastCommaIndex > lastDotIndex ? ',' : '.';
      const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
      const normalizedValue = compactValue
        .replaceAll(thousandsSeparator, '')
        .replace(decimalSeparator, '.');

      return /^\d+(?:\.\d{1,2})?$/.test(normalizedValue) ? normalizedValue : undefined;
    }

    const separator = lastCommaIndex >= 0 ? ',' : lastDotIndex >= 0 ? '.' : '';
    if (!separator) {
      return compactValue;
    }

    const parts = compactValue.split(separator);
    if (parts.some((part) => !part)) {
      return undefined;
    }

    const usesGroupedThousands = parts.length > 1 &&
      parts.slice(1).every((part) => part.length === 3);

    if (usesGroupedThousands) {
      return parts.join('');
    }

    if (parts.length === 2 && parts[1].length <= 2) {
      return `${parts[0]}.${parts[1]}`;
    }

    return undefined;
  }

  private async readErrorCode(response: Response): Promise<string> {
    const responseText = await response.text();

    if (!responseText) {
      return '';
    }

    try {
      const parsedError = JSON.parse(responseText) as { message?: string; error?: string; title?: string; detail?: string };
      return parsedError.error ?? parsedError.message ?? parsedError.detail ?? parsedError.title ?? responseText;
    } catch {
      return responseText;
    }
  }

  private getForbiddenIrrigatorLoginMessage(errorCode: string): string {
    if (errorCode === 'PendingPayments') {
      return 'El comunero tiene pagos pendientes y eso impide que acceda al sistema.';
    }

    return 'No tienes permisos para acceder al sistema con estos datos.';
  }
}
